import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { FileClock, ShoppingCart, Wallet, AlertCircle, Wrench, Archive, Users, CalendarDays, Inbox } from "lucide-react";
import { api, money, can, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Panel, Empty } from "@/components/common";
import { Progress } from "@/components/ui/progress";

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);

  if (!d) return <p className="text-sm text-slate-500">Chargement…</p>;
  const chart = d.by_category.map((c) => ({ ...c, name: CATEGORIES[c.category].label, color: CATEGORIES[c.category].color }));
  const comm = can(user, "commercial");
  const tech = can(user, "technicien");
  const rh = can(user, "rh");

  return (
    <div>
      <PageHeader kicker={`Bonjour, ${user.name}`} title="Tableau de bord" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {comm && <Link to="/demandes"><StatCard testId="stat-requests-new" label="Nouvelles demandes du site" value={d.requests_new} icon={Inbox} accent="#DC2626" /></Link>}
        {comm && <StatCard testId="stat-quotes-pending" label="Devis à valider" value={d.quotes_pending} icon={FileClock} accent="#D97706" />}
        {comm && <StatCard testId="stat-orders-open" label="Commandes ouvertes" value={d.orders_open} icon={ShoppingCart} />}
        {comm && <StatCard testId="stat-collected" label="Encaissé" value={money(d.collected_total)} icon={Wallet} accent="#059669" />}
        {comm && <StatCard testId="stat-outstanding" label="Reste à encaisser" value={money(d.outstanding_total)} icon={AlertCircle} accent="#DC2626" />}
        {tech && <StatCard testId="stat-interventions-active" label="Interventions actives" value={d.interventions_active} icon={Wrench} accent="#0284C7" />}
        {user.role === "admin" && <StatCard testId="stat-archived" label="Rapports archivés" value={d.interventions_archived} icon={Archive} accent="#0F172A" />}
        {rh && <StatCard testId="stat-employees" label="Employés actifs" value={d.employees} icon={Users} accent="#7C3AED" />}
        {rh && <StatCard testId="stat-leaves-pending" label="Congés en attente" value={d.leaves_pending} icon={CalendarDays} accent="#D97706" />}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Panel title="Activité par pôle de service (montant facturé)">
          <div className="h-72" data-testid="chart-by-category">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="invoiced" name="Facturé" radius={[3, 3, 0, 0]}>
                  {chart.map((c) => <Cell key={c.category} fill={c.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <div className="space-y-6">
          {comm && (
            <Panel title="Devis en attente de validation" action={<Link to="/devis" className="text-xs font-semibold text-red-600 hover:underline">Tout voir</Link>}>
              {d.pending_quotes.length ? (
                <ul className="divide-y divide-slate-100">
                  {d.pending_quotes.map((q) => (
                    <li key={q.id}>
                      <Link to={`/devis/${q.id}`} className="flex items-center justify-between py-2.5 text-sm hover:text-red-600" data-testid={`pending-quote-${q.id}`}>
                        <span><span className="font-mono text-xs text-slate-500">{q.number}</span> · {q.client_name}</span>
                        <span className="font-semibold">{money(q.total)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : <Empty text="Aucun devis en attente." />}
            </Panel>
          )}
          {tech && (
            <Panel title="Interventions en cours">
              {d.active_interventions.length ? (
                <ul className="space-y-3">
                  {d.active_interventions.map((i) => (
                    <li key={i.id}>
                      <Link to={`/interventions/${i.id}`} className="block text-sm hover:text-red-600">
                        <div className="flex justify-between"><span>{i.title}</span><span className="font-mono text-xs">{i.progress}%</span></div>
                        <Progress value={i.progress} className="mt-1.5 h-1.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : <Empty text="Aucune intervention active." />}
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
